import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { employeeAPI, roleAPI, authAPI } from '../services/api';
import { Loader2, Check } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { cn } from '../lib/utils';

const AssignRoleModal = ({ onClose, onSuccess }) => {
    const [employees, setEmployees] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    const [selectedRoleIds, setSelectedRoleIds] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [empResponse, roleResponse] = await Promise.all([
                    employeeAPI.getAllEmployees({ pageSize: 100 }), // Fetch first 100 for now
                    roleAPI.getAllRoles({ pageSize: 100 })
                ]);

                const empData = empResponse.data.content || empResponse.data;
                const roleData = roleResponse.data.content || roleResponse.data;

                setEmployees(Array.isArray(empData) ? empData : []);
                setRoles(Array.isArray(roleData) ? roleData : []);
            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Failed to load data. Please try again.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // When employee is selected, populate their current roles
    useEffect(() => {
        if (selectedEmployeeId) {
            const employee = employees.find(e => e.employeeId.toString() === selectedEmployeeId);
            if (employee && employee.roleDTOs) {
                setSelectedRoleIds(employee.roleDTOs.map(r => r.roleId));
            } else {
                setSelectedRoleIds([]);
            }
        } else {
            setSelectedRoleIds([]);
        }
    }, [selectedEmployeeId, employees]);

    const handleRoleToggle = (roleId) => {
        setSelectedRoleIds(prev => {
            if (prev.includes(roleId)) {
                return prev.filter(id => id !== roleId);
            } else {
                return [...prev, roleId];
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedEmployeeId) {
            setError("Please select an employee.");
            return;
        }
        if (selectedRoleIds.length === 0) {
            setError("Please select at least one role.");
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            await authAPI.assignRole(selectedEmployeeId, selectedRoleIds);
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Assign role error:", err);
            setError(err.response?.data?.message || "Failed to assign roles.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-md overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[2.5rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 dark:ring-white/10 dark:text-slate-100">
                <DialogHeader className="p-8 pb-4 border-b bg-white/40 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl sticky top-0 z-40">
                    <div className="flex items-center gap-4">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-[#FF7D00] to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative p-3 rounded-2xl bg-[#FF7D00]/10 text-[#FF7D00] shadow-sm transform hover:scale-105 transition-transform duration-300">
                                <Check className="w-6 h-6" />
                            </div>
                        </div>
                        <div>
                            <DialogTitle className="text-2xl font-black tracking-tight text-slate-800 dark:text-white uppercase">Assign Roles</DialogTitle>
                            <DialogDescription className="text-xs font-bold text-slate-500 dark:text-slate-400 mt-0.5 opacity-80">
                                Manage access levels and permissions for employees.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
                    </div>
                ) : (
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                        {error && (
                            <Alert variant="destructive" className="bg-red-50 text-red-600 border-red-200">
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-3">
                            <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1">Select Employee</Label>
                            <Select
                                value={selectedEmployeeId}
                                onValueChange={setSelectedEmployeeId}
                            >
                                <SelectTrigger className="jit-input w-full h-12">
                                    <SelectValue placeholder="Choose an employee..." />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-slate-200 dark:border-slate-800">
                                    {employees.map((emp) => (
                                        <SelectItem key={emp.employeeId} value={emp.employeeId.toString()} className="font-bold focus:bg-orange-50 focus:text-[#FF7D00] dark:focus:bg-[#FF7D00]/10">
                                            {emp.firstName} {emp.lastName} ({emp.username})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {selectedEmployeeId && (
                            <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                <Label className="text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 ml-1">Roles</Label>
                                <div className="grid grid-cols-1 gap-3 border rounded-3xl p-6 bg-slate-50 dark:bg-slate-900/50 max-h-[250px] overflow-y-auto border-slate-100 dark:border-slate-800 shadow-inner">
                                    {roles.map((role) => {
                                        const isSelected = selectedRoleIds.includes(role.roleId);
                                        return (
                                            <div
                                                key={role.roleId}
                                                onClick={() => handleRoleToggle(role.roleId)}
                                                className={`
                                                    flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all duration-300 transform active:scale-95
                                                    ${isSelected
                                                        ? 'bg-white dark:bg-slate-800 border-[#FF7D00] ring-4 ring-orange-500/10 shadow-lg'
                                                        : 'bg-white/40 dark:bg-white/5 border-slate-100 dark:border-slate-800 hover:border-orange-300 dark:hover:border-orange-500/30'}
                                                    border-2
                                                `}
                                            >
                                                <div className="flex flex-col">
                                                    <span className={cn("font-black text-sm uppercase tracking-wider", isSelected ? "text-[#FF7D00]" : "text-slate-600 dark:text-slate-400")}>{role.roleName}</span>
                                                </div>
                                                {isSelected && (
                                                    <div className="bg-[#FF7D00] rounded-full p-0.5 shadow-lg shadow-orange-500/20">
                                                        <Check className="h-4 w-4 text-white" />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {roles.length === 0 && (
                                        <p className="text-sm text-gray-500 text-center py-2">No roles available.</p>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500">
                                    Click on roles to select or deselect them.
                                </p>
                            </div>
                        )}

                        <DialogFooter className="p-6 pt-4 border-t bg-slate-50/50 dark:bg-slate-900/60 dark:border-slate-800 sticky bottom-0 z-30 sm:justify-end items-center gap-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
                            <Button type="button" variant="outline" onClick={onClose} className="h-10 px-6 rounded-xl border-slate-200 dark:border-slate-800 font-bold uppercase tracking-widest text-[10px] text-slate-500">
                                Discard
                            </Button>
                            <Button
                                type="submit"
                                className="h-10 px-8 rounded-xl bg-[#FF7D00] hover:brightness-110 active:scale-95 text-white font-black uppercase tracking-widest shadow-lg shadow-orange-500/20"
                                disabled={submitting || !selectedEmployeeId}
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Update Matrix'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AssignRoleModal;
